// src/pages/FlaggedContentPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { reportsService } from '../services/reportsService';

// Import unified components
import ModerationStats from '../components/reports/ModerationStats';
import ModeratedContentList from '../components/reports/ModeratedContentList';
import ReportReviewModal from '../components/reports/ReportReviewModal';

function FlaggedContentPage() {
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [stats, setStats] = useState({
    flagged: 0,
    highSeverity: 0,
    resolvedToday: 0
  });
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState({
    severity: '',
    timeframe: '24'
  });

  const fetchStats = async () => {
    console.log('[FlaggedContentPage] Fetching stats');
    try {
      const data = await reportsService.getFlaggedContentStats(24);
      console.log('[FlaggedContentPage] Received stats:', data);
      setStats({
        flagged: data.flaggedMessages || 0,
        highSeverity: data.highSeverity || 0,
        resolvedToday: data.resolvedFlags || 0
      });
    } catch (error) {
      console.error('[FlaggedContentPage] Error fetching stats:', error);
      setStats({
        flagged: 0,
        highSeverity: 0,
        resolvedToday: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = useCallback(async () => {
    console.log('[FlaggedContentPage] Fetching messages');
    setLoading(true);
    try {
      const result = await reportsService.getFlaggedContent({
        page: currentPage,
        limit: 20,
        severity: filter.severity,
        timeframe: filter.timeframe
      });

      setReports(result.reports || []);
      setTotalPages(result.totalPages || 1);
    } catch (error) {
      console.error('[FlaggedContentPage] Error fetching messages:', error);
      setReports([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filter]);

  useEffect(() => {
    fetchStats();
  }, []);
  
  useEffect(() => {
    fetchMessages();
  }, [currentPage, filter, fetchMessages]);

  const handleFilterChange = (key, value) => {
    setFilter(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  const handleReviewComplete = () => {
    fetchMessages();
    fetchStats();
    setSelectedReportId(null);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Flagged Messages</h1>
          <p className="mt-1 text-sm text-gray-500">
            Review and moderate automatically flagged messages
          </p>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="flex gap-4 items-center">
        <select
          className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
          value={filter.severity}
          onChange={(e) => handleFilterChange('severity', e.target.value)}
        >
          <option value="">All Severity</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>

        <select
          className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
          value={filter.timeframe}
          onChange={(e) => handleFilterChange('timeframe', e.target.value)}
        >
          <option value="24">Last 24 Hours</option>
          <option value="48">Last 48 Hours</option>
          <option value="72">Last 72 Hours</option>
          <option value="168">Last Week</option>
        </select>
      </div>



      {/* Stats Cards - Using unified component */}
      <ModerationStats stats={stats} contentType="message" />

      {/* Flagged Content List - Using unified list component */}
      <ModeratedContentList
        items={reports}
        loading={loading}
        contentType="message"
        onReviewClick={setSelectedReportId}
      />

      {/* Pagination */}
      {!loading && reports.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </p>
          <div className="flex space-x-2">
            <button
              className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 disabled:opacity-50"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <button
              className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 disabled:opacity-50"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Review Modal - Using unified component */}
      <ReportReviewModal
        reportId={selectedReportId}
        isOpen={!!selectedReportId}
        onClose={() => setSelectedReportId(null)}
        onReviewComplete={handleReviewComplete}
        contentType="message"
      />
    </div>
  );
}

export default FlaggedContentPage;